// frontend/src/pages/partner/SalesPartner.jsx
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import SalesEvolutionChart from '../../components/charts/SalesEvolutionChart';
import './SalesPartner.css';

const SalesPartner = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedService = queryParams.get('service');

  const [loading,      setLoading]      = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [stats,        setStats]        = useState({
    total_revenue: 0, total_sales: 0, unique_clients: 0, avg_basket: 0
  });
  const [dailyData,    setDailyData]    = useState([]);
  const [services,     setServices]     = useState([]);
  const [totalItems,   setTotalItems]   = useState(0);
  const [detailTx,     setDetailTx]     = useState(null);

  const [filters, setFilters] = useState({
    period:     '30j',
    start_date: '',
    end_date:   '',
    service_id: preselectedService || 'all',
    status:     'all',
    search:     ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  /* ── Fetch ───────────────────────────────────────────────── */
  const fetchServices = useCallback(async () => {
    try {
      const res = await API.get('/companies/my');
      setServices(res.data.companies || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await API.get('/transactions/stats', { params: { period: filters.period } });
      if (res.data.success) {
        setStats({
          total_revenue:  parseFloat(res.data.stats.total_revenue)  || 0,
          total_sales:    parseInt(res.data.stats.total_sales)       || 0,
          unique_clients: parseInt(res.data.stats.unique_clients)    || 0,
          avg_basket:     parseFloat(res.data.stats.avg_basket)      || 0,
        });
        setDailyData((res.data.daily || []).map(d => ({
          date:          d.date,
          daily_revenue: parseFloat(d.daily_revenue) || 0,
          daily_sales:   parseInt(d.daily_sales)     || 0,
        })));
      }
    } catch (err) { console.error(err); }
  }, [filters.period]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: itemsPerPage, offset: (currentPage - 1) * itemsPerPage };
      if (filters.start_date)            params.start_date = filters.start_date;
      if (filters.end_date)              params.end_date   = filters.end_date;
      if (filters.service_id !== 'all')  params.service_id = filters.service_id;
      if (filters.status     !== 'all')  params.status     = filters.status;
      const res = await API.get('/transactions/history', { params });
      if (res.data.success) {
        setTransactions((res.data.transactions || []).map(t => ({
          ...t,
          points_amount: parseInt(t.points_amount)   || 0,
          amount:        parseFloat(t.amount)         || 0,
        })));
        setTotalItems(res.data.total || 0);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  /* ── Helpers ─────────────────────────────────────────────── */
  const handleExportCSV = async () => {
    try {
      const params = {};
      if (filters.start_date)           params.start_date = filters.start_date;
      if (filters.end_date)             params.end_date   = filters.end_date;
      if (filters.service_id !== 'all') params.service_id = filters.service_id;
      const response = await API.get('/transactions/export', { params, responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `ventes_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const setFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const map = {
      completed: { cls: 'badge-success',   icon: 'fa-check-circle',  label: 'Réussi'      },
      pending:   { cls: 'badge-warning',   icon: 'fa-clock',         label: 'En attente'  },
      failed:    { cls: 'badge-danger',    icon: 'fa-times-circle',  label: 'Échoué'      },
      refunded:  { cls: 'badge-secondary', icon: 'fa-undo',          label: 'Remboursé'   },
    };
    const b = map[status] || { cls: 'badge-secondary', icon: 'fa-circle', label: status };
    return (
      <span className={`sp-badge ${b.cls}`}>
        <i className={`fas ${b.icon}`} /> {b.label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className="sp-page">

      {/* ── Header hero ────────────────────────────────────── */}
      <motion.div className="sp-header"
        initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}>
        <div className="sp-hero-orb sp-hero-orb-1" />
        <div className="sp-hero-orb sp-hero-orb-2" />
        <div className="sp-header-left">
          <div className="sp-hero-icon"><i className="fas fa-chart-line" /></div>
          <div>
            <h1>Ventes</h1>
            <p>Suivez et analysez vos performances commerciales</p>
          </div>
        </div>
        <div className="sp-header-actions">
          <button className="sp-btn-export" onClick={handleExportCSV}>
            <i className="fas fa-download" /> Exporter CSV
          </button>
          <div className="sp-period-pills">
            {['7j', '30j', '90j'].map(p => (
              <button
                key={p}
                className={`sp-period-pill ${filters.period === p ? 'active' : ''}`}
                onClick={() => setFilter('period', p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── KPI cards ──────────────────────────────────────── */}
      <div className="sp-kpi-grid">
        {[
          { cls: 'revenue', icon: 'fa-coins',         label: "Chiffre d'affaires", value: `${stats.total_revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} TND` },
          { cls: 'sales',   icon: 'fa-shopping-cart', label: 'Nombre de ventes',   value: stats.total_sales.toLocaleString() },
          { cls: 'clients', icon: 'fa-users',         label: 'Clients uniques',     value: stats.unique_clients.toLocaleString() },
          { cls: 'avg',     icon: 'fa-chart-simple',  label: 'Panier moyen',        value: `${stats.avg_basket.toFixed(2)} TND` },
        ].map((kpi, i) => (
          <motion.div
            key={kpi.cls}
            className={`sp-kpi-card sp-kpi-card--${kpi.cls}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -4 }}
          >
            <div className="sp-kpi-bg-icon"><i className={`fas ${kpi.icon}`} /></div>
            <div className="sp-kpi-label">{kpi.label}</div>
            <div className="sp-kpi-value">{kpi.value}</div>
          </motion.div>
        ))}
      </div>

      {/* ── Filters bar ────────────────────────────────────── */}
      <div className="sp-filters-bar">
        <div className="sp-filter-group">
          <select className="sp-filter-select"
            value={filters.service_id}
            onChange={e => setFilter('service_id', e.target.value)}>
            <option value="all">Tous les services</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="sp-filter-select"
            value={filters.status}
            onChange={e => setFilter('status', e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="completed">Réussis</option>
            <option value="pending">En attente</option>
            <option value="failed">Échoués</option>
          </select>
        </div>
        <div className="sp-date-range">
          <input type="date" className="sp-date-input"
            value={filters.start_date}
            onChange={e => setFilter('start_date', e.target.value)} />
          <span className="sp-date-arrow">→</span>
          <input type="date" className="sp-date-input"
            value={filters.end_date}
            onChange={e => setFilter('end_date', e.target.value)} />
        </div>
        <input type="text" className="sp-search-input"
          placeholder="Rechercher client, transaction…"
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)} />
      </div>

      
      <SalesEvolutionChart
  data={dailyData}
  period={filters.period}
  onPeriodChange={(newPeriod) => setFilter('period', newPeriod)}
/>

      {/* ── Tableau des transactions ───────────────────────── */}
      <div className="sp-table-card">
        <div className="sp-table-header">
          <h3><i className="fas fa-list-ul" /> Détail des transactions</h3>
          <span className="sp-tx-count">{totalItems} transaction{totalItems !== 1 ? 's' : ''}</span>
        </div>

        <div className="sp-table-wrap">
          <table className="sp-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Client</th>
                <th>Service</th>
                <th>Points</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="sp-row-loading">
                  <div className="sp-inline-loader" />
                </td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan="8" className="sp-row-empty">Aucune transaction trouvée</td></tr>
              ) : (
                transactions.map((tx, i) => (
                  <motion.tr key={tx.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}>
                    <td className="sp-td-id">#{tx.id}</td>
                    <td className="sp-td-date">
                      {new Date(tx.created_at).toLocaleString('fr-FR', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </td>
                    <td>
                      <div className="sp-client">
                        <i className="fas fa-user-circle" />
                        {tx.client_first_name || ''} {tx.client_last_name || ''}
                      </div>
                    </td>
                    <td>{tx.company_name || '—'}</td>
                    <td className="sp-td-points">{tx.points_amount.toLocaleString()} pts</td>
                    <td className="sp-td-amount">{tx.amount.toFixed(2)} TND</td>
                    <td>{getStatusBadge(tx.status)}</td>
                    <td>
                      <button className="sp-btn-detail" onClick={() => setDetailTx(tx)}>
                        <i className="fas fa-eye" /> Détail
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="sp-pagination">
            <button className="sp-page-btn" disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}>
              <i className="fas fa-chevron-left" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 5)          p = i + 1;
              else if (currentPage <= 3)    p = i + 1;
              else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
              else                          p = currentPage - 2 + i;
              return (
                <button key={p}
                  className={`sp-page-btn ${currentPage === p ? 'active' : ''}`}
                  onClick={() => setCurrentPage(p)}>{p}</button>
              );
            })}
            <button className="sp-page-btn" disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}>
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* ── Modal détail transaction ───────────────────────── */}
      <AnimatePresence>
        {detailTx && (
          <motion.div className="sp-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDetailTx(null)}>
            <motion.div className="sp-detail-modal"
              initial={{ opacity: 0, y: -20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              onClick={e => e.stopPropagation()}>
              <div className="sp-detail-header">
                <h3>Transaction #{detailTx.id}</h3>
                <button className="sp-detail-close" onClick={() => setDetailTx(null)}>
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="sp-detail-body">
                {[
                  ['Client',   `${detailTx.client_first_name || ''} ${detailTx.client_last_name || ''}`.trim() || '—'],
                  ['Service',  detailTx.company_name || '—'],
                  ['Points',   `${detailTx.points_amount.toLocaleString()} pts`],
                  ['Montant',  `${detailTx.amount.toFixed(2)} TND`],
                  ['Statut',   null],
                  ['Date',     new Date(detailTx.created_at).toLocaleString('fr-FR')],
                  ['Paiement', detailTx.stripe_payment_id || '—'],
                ].map(([label, val]) => (
                  <div key={label} className="sp-detail-row">
                    <span className="sp-detail-label">{label}</span>
                    <span className="sp-detail-val">
                      {label === 'Statut' ? getStatusBadge(detailTx.status) : val}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default SalesPartner;